import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Pagination, Select, Space, message } from 'antd';
import documentService from '../../services/documentService';
import ConvertInspectionToEventModal from './ConvertInspectionToEventModal';
import DocumentList from './DocumentList';
import PendingDocuments from './PendingDocuments';
import SearchInput from '../inspections/SearchInput';
import PullToRefresh from '../PullToRefresh';
import '../../assets/styles/documents.css';

const PAGE_SIZE = 20;

/** The V2_DocumentTypes row the inspection scope is limited to. */
const INSPECTION_TYPE = 'Inspection';

const renderFilterOption = (option) => (
  <span className="document-library__option">
    <span className="document-library__option-name">{option.data.label}</span>
    <span className="document-library__option-count">({option.data.count})</span>
  </span>
);

const DocumentBrowser = ({
  scope = 'documents',
  status,
  title,
  embedded = false,
  showFilters = false,
  showPending = false,
  returnTo,
  completedReturnTo,
  inProgressReturnTo,
  rowTitle,
  placeholder = 'Search documents',
  emptyText = 'Nothing here yet.',
}) => {
  const navigate = useNavigate();
  const inspectionsOnly = scope === 'inspections';

  const [documents, setDocuments] = useState([]);
  const [filterCounts, setFilterCounts] = useState({ types: [], templates: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeIds, setTypeIds] = useState([]);
  const [statusIds, setStatusIds] = useState([]);
  const [page, setPage] = useState({ current: 0, total: 0 });
  const [templates, setTemplates] = useState([]);
  const [convertDocumentId, setConvertDocumentId] = useState(null);
  const offersEvents = inspectionsOnly && status === 'completed';

  useEffect(() => {
    if (!showFilters) return undefined;

    let cancelled = false;

    documentService
      .getAvailableTemplates()
      .then((items) => {
        if (!cancelled) setTemplates(items);
      })
      .catch((error) => {
        console.error('Failed to load template lookups', error);
        if (!cancelled) setTemplates([]);
      });

    return () => {
      cancelled = true;
    };
  }, [showFilters]);

  const typeNames = useMemo(() => {
    const byId = new Map();
    templates.forEach((template) => {
      if (template.typeId != null) byId.set(String(template.typeId), template.typeName);
    });
    return byId;
  }, [templates]);

  const statusNames = useMemo(() => {
    const byId = new Map();
    templates.forEach((template) => {
      (template.statuses ?? []).forEach((entry) => {
        byId.set(String(entry.id), entry.statusName);
      });
    });
    return byId;
  }, [templates]);

  const load = useCallback(
    async (pageIndex = 0, isSearch = false) => {
      if (isSearch) setSearchLoading(true);
      else setLoading(true);

      try {
        const params = {
          status,
          pageIndex,
          pageSize: PAGE_SIZE,
          search: search.trim() || undefined,
        };

        const response = inspectionsOnly
          ? await documentService.getInspectionDocuments(params)
          : await documentService.getDocuments({ ...params, typeIds, statusIds });

        setDocuments(response?.items ?? []);
        setFilterCounts(response?.filterCounts ?? { types: [], templates: [], statuses: [] });
        setPage({
          current: response?.currentPage ?? 0,
          total: response?.totalCount ?? 0,
        });
      } catch (error) {
        console.error('Failed to load documents', error);
        message.error('Failed to load documents');
        setDocuments([]);
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    },
    [inspectionsOnly, status, search, typeIds, statusIds]
  );

  useEffect(() => {
    load(0, true);
  }, [load]);

  const handleSelect = (document) => {
    navigate(`/documents/${document.id}`, {
      state: {
        templateName: document.name,
        returnTo,
        completedReturnTo,
        inProgressReturnTo,
        pageTitle: rowTitle,
      },
    });
  };

  const handleEvent = (document) => {
    if (document.eventId) {
      navigate(`/events/id/${document.eventId}`);
      return;
    }
    setConvertDocumentId(document.id);
  };

  const handleViewPdf = async (document) => {
    // A Word-template document is filled and converted on request, which takes a
    // few seconds, so say something while it happens.
    const loading = document.usesWordTemplate
      ? message.loading('Preparing the PDF...', 0)
      : null;

    try {
      const url = document.usesWordTemplate
        ? await documentService.getDocumentWordPdf(document.id)
        : await documentService.getDocumentPdf(document.id);

      if (!url) {
        message.info('No PDF has been generated for this document yet.');
        return;
      }
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Failed to open document PDF', error);
      message.error('Failed to open PDF');
    } finally {
      loading?.();
    }
  };

  return (
    <PullToRefresh onRefresh={() => load(page.current)}>
      <div className={embedded ? undefined : 'document-library'}>
        {showPending && <PendingDocuments typeName={inspectionsOnly ? INSPECTION_TYPE : null} />}

        <Card className="document-library__panel" title={title}>
          <Space
            direction="vertical"
            size={10}
            className="document-library__controls"
            style={{ width: '100%' }}
          >
            <SearchInput
              value={search}
              onChange={setSearch}
              loading={searchLoading}
              placeholder={placeholder}
            />

            {showFilters && (
              <Space size={8} wrap style={{ width: '100%' }}>
                <Select
                  mode="multiple"
                  allowClear
                  className="document-library__filter"
                  placeholder="Type"
                  value={typeIds}
                  onChange={setTypeIds}
                  optionRender={renderFilterOption}
                  options={(filterCounts.types ?? []).map((entry) => ({
                    value: Number(entry.key),
                    label: typeNames.get(String(entry.key)) ?? 'Unnamed type',
                    count: entry.count,
                  }))}
                />
                <Select
                  mode="multiple"
                  allowClear
                  className="document-library__filter"
                  placeholder="Status"
                  value={statusIds}
                  onChange={setStatusIds}
                  optionRender={renderFilterOption}
                  options={(filterCounts.statuses ?? []).map((entry) => ({
                    value: Number(entry.key),
                    label: statusNames.get(String(entry.key)) ?? 'Unnamed status',
                    count: entry.count,
                  }))}
                />
              </Space>
            )}
          </Space>

          <DocumentList
            documents={documents}
            loading={loading}
            onSelect={handleSelect}
            onViewPdf={handleViewPdf}
            onEvent={offersEvents ? handleEvent : undefined}
            emptyText={emptyText}
          />

          {page.total > PAGE_SIZE && (
            <Pagination
              className="document-library__pagination"
              simple
              current={page.current + 1}
              pageSize={PAGE_SIZE}
              total={page.total}
              onChange={(next) => load(next - 1)}
            />
          )}
        </Card>

        {offersEvents && (
          <ConvertInspectionToEventModal
            documentId={convertDocumentId}
            onClose={() => setConvertDocumentId(null)}
            onConverted={() => load(page.current)}
          />
        )}
      </div>
    </PullToRefresh>
  );
};

export default DocumentBrowser;
